import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Trophy, Crown, Medal, TrendUp, TrendDown, Users, Lightning, ArrowLeft, Clock, Sun, Moon } from '@phosphor-icons/react';
import axios from 'axios';
import BrandLogo from '@/components/BrandLogo';
import { useTheme } from '@/contexts/ThemeContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function RankBadge({ rank }) {
  if (rank === 1) return <Crown size={22} weight="fill" className="text-[#F59E0B]" />;
  if (rank === 2) return <Medal size={22} weight="fill" className="text-[#C0C0C0]" />;
  if (rank === 3) return <Medal size={22} weight="fill" className="text-[#CD7F32]" />;
  return <span className="font-mono text-sm text-[var(--oh-text-muted)] w-6 text-center">{rank}</span>;
}

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export default function LeaderboardPage() {
  const { user } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [leaderboard, setLeaderboard] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('leaderboard');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [lbRes, actRes] = await Promise.all([
          axios.get(`${API}/leaderboard`),
          axios.get(`${API}/activity`),
        ]);
        setLeaderboard(lbRes.data);
        setActivity(actRes.data);
      } catch (err) {
        console.error('Failed to load leaderboard:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-[var(--oh-bg)] grid-bg" data-testid="leaderboard-page">
      {/* Header for non-authenticated */}
      {!user && (
        <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl border-b border-[var(--oh-border)] h-16" style={{ backgroundColor: 'var(--oh-glass)' }}>
          <div className="max-w-7xl mx-auto h-full px-4 md:px-8 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <BrandLogo variant="header" />
            </Link>
            <div className="flex items-center gap-3">
              <button onClick={toggleTheme} className="w-9 h-9 rounded-lg flex items-center justify-center border border-[var(--oh-border)]" style={{ backgroundColor: 'var(--oh-bg-secondary)' }}>
                {isDark ? <Sun size={16} className="text-[#F59E0B]" weight="fill" /> : <Moon size={16} className="text-[#6366f1]" weight="fill" />}
              </button>
              <button onClick={() => navigate('/auth')} className="bg-gradient-to-r from-[#00FFA3] to-[#00B8FF] text-black font-bold text-sm px-6 py-2 rounded-lg hover:scale-[1.02] transition-transform">
                Get Started
              </button>
            </div>
          </div>
        </header>
      )}

      <div className={`${user ? 'pt-24' : 'pt-24'} px-4 md:px-8 pb-12`}>
        <div className="max-w-5xl mx-auto">
          {/* Back Link */}
          <button onClick={() => navigate(user ? '/markets' : '/')} className="flex items-center gap-2 text-[var(--oh-text-secondary)] hover:text-white transition-colors mb-6" data-testid="leaderboard-back">
            <ArrowLeft size={18} /> {user ? 'Back to Markets' : 'Back to Home'}
          </button>

          {/* Page Title */}
          <div className="mb-8 animate-fade-up">
            <div className="flex items-center gap-3 mb-2">
              <Trophy size={32} className="text-[#F59E0B]" weight="fill" />
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-[var(--oh-text)] tracking-tight">Leaderboard</h1>
            </div>
            <p className="text-[var(--oh-text-secondary)] text-base">Top traders ranked by profit. Trade smart, climb the ranks.</p>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6 animate-fade-up" style={{ animationDelay: '0.1s' }}>
            <button onClick={() => setTab('leaderboard')} data-testid="tab-leaderboard"
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all border ${
                tab === 'leaderboard'
                  ? 'bg-[rgba(0,255,163,0.1)] text-[#00FFA3] border-[rgba(0,255,163,0.3)]'
                  : 'bg-[var(--oh-card)] text-[var(--oh-text-secondary)] border-[var(--oh-border)] hover:border-[var(--oh-border-hover)] hover:text-white'
              }`}>
              <Trophy size={14} className="inline mr-2" weight="fill" /> Rankings
            </button>
            <button onClick={() => setTab('activity')} data-testid="tab-activity"
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all border ${
                tab === 'activity'
                  ? 'bg-[rgba(0,184,255,0.1)] text-[#00B8FF] border-[rgba(0,184,255,0.3)]'
                  : 'bg-[var(--oh-card)] text-[var(--oh-text-secondary)] border-[var(--oh-border)] hover:border-[var(--oh-border-hover)] hover:text-white'
              }`}>
              <Lightning size={14} className="inline mr-2" weight="fill" /> Live Activity
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-oh-green border-t-transparent rounded-full animate-spin" />
            </div>
          ) : tab === 'leaderboard' ? (
            <div className="animate-fade-up" style={{ animationDelay: '0.15s' }}>
              {leaderboard.length === 0 ? (
                <div className="bg-[var(--oh-card)] border border-[var(--oh-border)] rounded-xl p-12 text-center">
                  <Trophy size={48} className="mx-auto mb-4 text-[var(--oh-text-muted)]" />
                  <p className="text-[var(--oh-text-secondary)] text-lg mb-2">No rankings yet</p>
                  <p className="text-[var(--oh-text-muted)] text-sm">Rankings are based on resolved market trades. Start trading to appear here!</p>
                </div>
              ) : (
                <>
                  {/* Top 3 Podium */}
                  {leaderboard.length >= 3 && (
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      {[leaderboard[1], leaderboard[0], leaderboard[2]].map((trader, i) => {
                        const order = [2, 1, 3];
                        const colors = { 1: '#F59E0B', 2: '#C0C0C0', 3: '#CD7F32' };
                        const c = colors[order[i]];
                        const isFirst = order[i] === 1;
                        return (
                          <div key={trader.rank} className={`bg-[var(--oh-card)] border rounded-xl p-5 text-center ${isFirst ? 'border-[#F59E0B]/30 -mt-4 pb-8' : 'border-[var(--oh-border)] mt-2'}`}>
                            <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center text-black font-bold text-lg" style={{ backgroundColor: c }}>
                              {trader.name[0].toUpperCase()}
                            </div>
                            <RankBadge rank={order[i]} />
                            <Link to={`/profile/${trader.user_id}`} className="font-heading text-sm text-[var(--oh-text)] mt-2 truncate block hover:text-[#00FFA3] transition-colors">{trader.name}</Link>
                            <p className="text-xs text-[var(--oh-text-muted)]">{trader.country}</p>
                            <p className={`font-mono text-lg font-bold mt-2 ${trader.total_profit >= 0 ? 'text-[#00FFA3]' : 'text-[#FF3B30]'}`}>
                              {trader.total_profit >= 0 ? '+' : ''}${trader.total_profit.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                            </p>
                            <p className="text-xs text-[var(--oh-text-muted)] mt-1">{trader.win_rate}% win rate</p>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Full Rankings Table */}
                  <div className="bg-[var(--oh-card)] border border-[var(--oh-border)] rounded-xl overflow-hidden">
                    <div className="grid grid-cols-[auto_1fr_auto_auto_auto_auto] gap-4 px-5 py-3 border-b border-[var(--oh-border)] text-xs uppercase tracking-[0.15em] font-bold text-[var(--oh-text-muted)]">
                      <span>Rank</span>
                      <span>Trader</span>
                      <span className="text-right">Profit</span>
                      <span className="text-right hidden sm:block">Trades</span>
                      <span className="text-right hidden md:block">Win Rate</span>
                      <span className="text-right hidden md:block">Invested</span>
                    </div>
                    {leaderboard.map((trader) => {
                      const profitColor = trader.total_profit >= 0 ? 'text-[#00FFA3]' : 'text-[#FF3B30]';
                      return (
                        <div key={trader.rank} className="grid grid-cols-[auto_1fr_auto_auto_auto_auto] gap-4 px-5 py-4 border-b border-[var(--oh-border)] last:border-0 hover:bg-[var(--oh-bg-tertiary)] transition-colors items-center"
                          data-testid={`leaderboard-row-${trader.rank}`}>
                          <div className="w-8 flex justify-center"><RankBadge rank={trader.rank} /></div>
                          <div className="min-w-0">
                            <Link to={`/profile/${trader.user_id}`} className="text-sm text-[var(--oh-text)] font-medium truncate block hover:text-[#00FFA3] transition-colors">{trader.name}</Link>
                            <p className="text-xs text-[var(--oh-text-muted)]">{trader.country}</p>
                          </div>
                          <p className={`font-mono text-sm font-bold text-right ${profitColor}`}>
                            {trader.total_profit >= 0 ? '+' : ''}${trader.total_profit.toFixed(2)}
                          </p>
                          <p className="font-mono text-sm text-[var(--oh-text-secondary)] text-right hidden sm:block">{trader.total_trades}</p>
                          <p className="font-mono text-sm text-[var(--oh-text-secondary)] text-right hidden md:block">{trader.win_rate}%</p>
                          <p className="font-mono text-sm text-[var(--oh-text-muted)] text-right hidden md:block">${trader.total_invested.toFixed(2)}</p>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          ) : (
            /* Activity Feed */
            <div className="space-y-2 animate-fade-up" style={{ animationDelay: '0.15s' }}>
              {activity.length === 0 ? (
                <div className="bg-[var(--oh-card)] border border-[var(--oh-border)] rounded-xl p-12 text-center">
                  <Lightning size={48} className="mx-auto mb-4 text-[var(--oh-text-muted)]" />
                  <p className="text-[var(--oh-text-secondary)] text-lg mb-2">No activity yet</p>
                  <p className="text-[var(--oh-text-muted)] text-sm">Trades will appear here as they happen.</p>
                </div>
              ) : (
                activity.map((a, i) => {
                  const posColor = a.position === 'yes' ? '#00FFA3' : '#00B8FF';
                  const statusLabel = a.status === 'won' ? 'Won' : a.status === 'lost' ? 'Lost' : a.status === 'sold' ? 'Sold' : 'Active';
                  const statusColor = a.status === 'won' ? '#00FFA3' : a.status === 'lost' ? '#FF3B30' : a.status === 'sold' ? '#F59E0B' : '#A1A1AA';
                  return (
                    <div key={i} className="bg-[var(--oh-card)] border border-[var(--oh-border)] rounded-xl px-5 py-4 hover:border-[var(--oh-border-hover)] transition-colors flex items-center gap-4"
                      data-testid={`activity-item-${i}`}>
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#00FFA3] to-[#00B8FF] flex items-center justify-center text-black font-bold text-xs flex-shrink-0">
                        {a.user_name[0].toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-[var(--oh-text)]">
                          <Link to={`/profile/${a.user_id}`} className="font-medium hover:text-[#00FFA3] transition-colors">{a.user_name}</Link>
                          <span className="text-[var(--oh-text-muted)]"> placed </span>
                          <span className="font-bold uppercase" style={{ color: posColor }}>{a.position}</span>
                          <span className="text-[var(--oh-text-muted)]"> for </span>
                          <span className="font-mono text-[var(--oh-text)]">${a.amount.toFixed(2)}</span>
                        </p>
                        <p className="text-xs text-[var(--oh-text-muted)] truncate mt-0.5">{a.market_question}</p>
                      </div>
                      <div className="flex flex-col items-end flex-shrink-0">
                        <span className="text-xs font-bold uppercase" style={{ color: statusColor }}>{statusLabel}</span>
                        <span className="text-xs text-[var(--oh-text-muted)] flex items-center gap-1 mt-0.5">
                          <Clock size={10} /> {timeAgo(a.created_at)}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
